import _ from 'lodash'
import { UserGameHandState, buildUserGameHandStates } from './buildUserGameHandStates'
import { ID, Model, Logger } from '../types'

export abstract class AbstractGameHandController {
  protected userStateMap: Record<ID, UserGameHandState>
  userStates: UserGameHandState[]
  maxAmountBetInCurrentRound: number
  otherUsersTablePosition: ID[]
  usersInGameTablePosition: ID[]

  constructor(public gameHand: Model.GameHand, public currentUserId: string) {
    this.userStateMap = buildUserGameHandStates(gameHand, currentUserId)
    this.userStates = this.gameHand.userTablePosition.map((userId) => this.userStateMap[userId])
    this.maxAmountBetInCurrentRound = _.maxBy(
      this.userStates,
      (state) => state.amountInvestedInCurrentRound,
    )!.amountInvestedInCurrentRound
    const userIndex = this.gameHand.userTablePosition.findIndex(
      (userId) => userId === currentUserId,
    )
    this.otherUsersTablePosition = [
      ...this.gameHand.userTablePosition.slice(userIndex + 1),
      ...this.gameHand.userTablePosition.slice(0, userIndex),
    ]
    this.usersInGameTablePosition = this.gameHand.userTablePosition.filter((userId) => {
      const state = this.userStateMap[userId]
      return state.tableState !== 'OUT'
    })
  }

  protected getUserState(userId: ID) {
    return this.userStateMap[userId]
  }

  get currentBettingRound() {
    return this.gameHand.bettingRounds[this.gameHand.bettingRounds.length - 1]
  }

  get isFirstRound() {
    return this.gameHand.bettingRounds.length === 1
  }

  amountRemainingForUserToCall(userId: string) {
    const state = this.getUserState(userId)
    return this.maxAmountBetInCurrentRound - state.amountInvestedInCurrentRound
  }

  canUserCheck(userId: string) {
    return !this.amountRemainingForUserToCall(userId)
  }

  canUserCall(userId: string) {
    const state = this.getUserState(userId)
    return (
      !!this.amountRemainingForUserToCall(userId) &&
      state.amountLeftToBet > this.amountRemainingForUserToCall(userId)
    )
  }

  isWaitingToCallTableBlinds(userId: string) {
    const state = this.getUserState(userId)
    return (
      state.amountInvested < this.gameHand.rules.blindAmount &&
      this.amountRemainingForUserToCall(userId) <= this.gameHand.rules.blindAmount
    )
  }

  canUserRaise(userId: string) {
    const state = this.getUserState(userId)
    return state.amountLeftToBet >= this.amountRemainingForUserToCall(userId)
  }

  userBetOptions(userId: string) {
    const state = this.getUserState(userId)
    if (state.state === 'ALL_IN' || state.state === 'FOLDED' || state.state === 'OUT') {
      return null
    }
    if (this.amountRemainingForUserToCall(userId) && !this.isWaitingToCallTableBlinds(userId)) {
      if (state.amountLeftToBet <= this.amountRemainingForUserToCall(userId)) {
        return 'ALL_IN'
      }
      return 'RAISE'
    }
    return 'BET'
  }

  private _minRaise() {
    switch (this.gameHand.rules.gameType) {
      case 'TEXAS_HOLDEM_NO_LIMIT': {
        const sortedAmountInvestedInCurrentRound = _.uniq(
          this.userStates.map((state) => state.amountInvestedInCurrentRound),
        )
        sortedAmountInvestedInCurrentRound.sort((amount1, amount2) => amount2 - amount1)
        const differenceBetweenTwoLargestAmountsInPot =
          sortedAmountInvestedInCurrentRound[0] - (sortedAmountInvestedInCurrentRound[1] || 0)
        return _.max([differenceBetweenTwoLargestAmountsInPot, this.gameHand.rules.blindAmount])
      }
      case 'TEXAS_HOLDEM_LIMIT': {
        if (this.gameHand.bettingRounds.length <= 1) {
          return this.gameHand.rules.blindAmount
        }
        return this.gameHand.rules.blindAmount * 2
      }
    }
  }

  minRaiseForUser(userId: string) {
    const min = _.min([
      this._minRaise(),
      this.getUserState(userId).amountLeftToBet - this.amountRemainingForUserToCall(userId),
    ])!
    return _.max([min, 0])!
  }

  private _maxRaise(userId: string) {
    const userState = this.getUserState(userId)
    switch (this.gameHand.rules.gameType) {
      case 'TEXAS_HOLDEM_NO_LIMIT':
        return userState.amountLeftToBet
      case 'TEXAS_HOLDEM_LIMIT': {
        if (this.gameHand.bettingRounds.length <= 1) {
          return this.gameHand.rules.blindAmount
        }
        return this.gameHand.rules.blindAmount * 2
      }
    }
  }

  maxRaiseForUser(userId: string) {
    const max = _.min([
      this._maxRaise(userId),
      this.getUserState(userId).amountLeftToBet - this.amountRemainingForUserToCall(userId),
    ])!
    return _.max([max, 0])!
  }

  getWinningUserId() {
    const entity = _.maxBy(
      _.entries(this.gameHand.finalResult?.gameHasEndedChipAmounts),
      ([_userId, amount]) => amount,
    )!
    const [userId] = entity
    return userId
  }
}
